import { Configuration } from "@seriously-rad/seriouslyrad-framework-core/Configuration";
import { DataSource } from "@seriously-rad/seriouslyrad-framework-core/Data/DataSource";
import { EntityCommandTypeEnum } from "@seriously-rad/seriouslyrad-framework-core/EntityCommand";
import { Constructor } from "@seriously-rad/seriouslyrad-framework-core/Extensions";
import { LinqArray } from "@seriously-rad/seriouslyrad-framework-core/LinqArray";
import { WindowShim } from "@seriously-rad/seriouslyrad-framework-core/RadWindow";
import { registerPageManagerConfiguration } from "@seriously-rad/seriouslyrad-framework-core/Registration";
import { DotNetTypeMapper } from "@seriously-rad/seriouslyrad-framework-core/Server/DotNet/DotNetTypeMapper";
import { ServiceManager } from "@seriously-rad/seriouslyrad-framework-core/ServiceManager";
import { ClassEditor } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ClassEditor";
import { IInputControl } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/InputControl";
import { EntityCommandPrintReportTemplateDesign } from "./EntityCommands/EntityCommandPrintReportTemplateDesign";
import { EntityCommandReportRunReport } from "./EntityCommands/EntityCommandReportRunReport";
import { EntityCommandReportScheduleReport } from "./EntityCommands/EntityCommandReportScheduleReport";
import { EntityCommandUserReportViewReport } from "./EntityCommands/EntityCommandUserReportViewReport";
import { GetDesignerInfoResponseService } from "./Server/PrintReportDesignerDataService";
import { ComboBoxReportStoredProcedureProperties } from "./Ui/Controls/PrintReportDesigner/Controls/ComboBoxReportStoredProcedureProperties";
import { ReportElement } from "./Ui/Controls/PrintReportDesigner/Controls/ReportElement";
import { PrintReportDesignerStatic } from "./Ui/Controls/PrintReportDesigner/PrintReportDesignerStatic";
import pageManagerConfiguration from "./Ui/PageManagerConfiguration";

import "./PrintReportDesigner.scss";

const
    radWindow = WindowShim();

export async function registerModuleReportsAsync(configuration: Configuration)
{
    registerDotNetTypes();
    registerReportElements();

    // Reports
    radWindow.entityManager?.registerCommand(new EntityCommandPrintReportTemplateDesign());
    radWindow.entityManager?.registerCommand(new EntityCommandReportRunReport());
    radWindow.entityManager?.registerCommand(new EntityCommandReportScheduleReport());

    // User Reports
    radWindow.entityManager?.removeCommand({
        type: EntityCommandTypeEnum.entitySet,
        entityName: "UserReport",
        key: "NEW"
    });
    radWindow.entityManager?.removeCommand({
        type: EntityCommandTypeEnum.entitySet,
        entityName: "UserReport",
        key: "FILTER"
    });
    radWindow.entityManager?.removeCommand({
        type: EntityCommandTypeEnum.entity,
        entityName: "UserReport",
        key: "OPEN"
    });
    radWindow.entityManager?.removeCommand({
        type: EntityCommandTypeEnum.entity,
        entityName: "UserReport",
        key: "DELETE"
    });

    radWindow.entityManager?.registerCommand(new EntityCommandUserReportViewReport());

    function createDesignerInfoListDataSource(key: string)
    {
        const
            designerInfo = ServiceManager.getRequired(GetDesignerInfoResponseService),
            enumValues = (designerInfo && key && (designerInfo as any)[key]) ?? null;

        return new DataSource({
            data:
                enumValues
                    ? Object.keys(enumValues).map(item => ({ value: item, description: enumValues[item] }))
                    : []
        });
    }

    const
        inputControlLoaderMap: { [key: string]: { [key: string]: () => Promise<IInputControl> } } = {
            "@seriously-rad/seriouslyrad-framework-module-reports/Ui/Controls/PrintReportDesigner/Controls/ReportElements/DrawingElements/BoxElement.BoxElementTemplateProperties": {
                fillColor: async () => new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ColorPicker")).ColorPicker(),
            },
            "@seriously-rad/seriouslyrad-framework-module-reports/Ui/Controls/PrintReportDesigner/Controls/ReportElements/DrawingElement.DrawingElementTemplateProperties": {
                lineColor: async () => new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ColorPicker")).ColorPicker(),
            },
            "@seriously-rad/seriouslyrad-framework-module-reports/Ui/Controls/PrintReportDesigner/Controls/ReportElements/FieldElement.FieldElementTemplateProperties": {
                color: async () => new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ColorPicker")).ColorPicker(),
            },
            "@seriously-rad/seriouslyrad-framework-module-reports/Ui/Controls/PrintReportDesigner/Controls/ReportTemplate.ReportTemplate": {
                paperOrientation: async () => new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ComboBox")).ComboBox({ listDataSource: createDesignerInfoListDataSource("paperOrientations") }),
                paperSize: async () => new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ComboBox")).ComboBox({ listDataSource: createDesignerInfoListDataSource("paperSizes") }),
                margin: async () => new (await import("./Ui/Controls/MarginEditor")).MarginEditor(),
            },
            "@seriously-rad/seriouslyrad-framework-module-reports/Ui/Controls/PrintReportDesigner/Controls/ReportElements/TextElement.TextElementTemplateProperties": {
                color: async () => new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ColorPicker")).ColorPicker(),
            },
            "@seriously-rad/seriouslyrad-framework-module-reports/Ui/Controls/PrintReportDesigner/Controls/ReportSection.ReportSectionTemplateProperties": {
                backgroundColor: async () => new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ColorPicker")).ColorPicker(),
            },
        };

    ClassEditor.registerOverride(async (propertyInfo) =>
    {
        for (const key in inputControlLoaderMap)
        {
            if (propertyInfo.typeInfo?.isAssignableTo(key) && inputControlLoaderMap[key][propertyInfo.name])
                return inputControlLoaderMap[key][propertyInfo.name]();
        }

        const
            classHierarchy = LinqArray.create(propertyInfo.typeInfo?.classHierarchy ?? []);

        if (classHierarchy.contains("SeriouslyRAD.Framework.Data.Reports.Print.ReportElements.FieldElementDatabase, SeriouslyRAD.Framework.Data.Reports"))
        {
            switch (propertyInfo.name)
            {
                // General
                case "color":
                    return new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ColorPicker")).ColorPicker();
                case "dataSourceFieldName":
                    return new ComboBoxReportStoredProcedureProperties();
                case "dataSourceFieldTypeId":
                    return (
                        new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ComboBox"))
                            .ComboBox({
                                listDataSource: new DataSource({
                                    data: [
                                        { value: "System.Boolean, System.Private.CoreLib", description: "Boolean" },
                                        { value: "System.DateTime, System.Private.CoreLib", description: "Date / Time" },
                                        { value: "System.Double, System.Private.CoreLib", description: "Double" },
                                        { value: "System.String, System.Private.CoreLib", description: "String" },
                                    ]
                                })
                            })
                    );

                // Boolean
                case "booleanFormat":
                    return new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ComboBox")).ComboBox({ listDataSource: createDesignerInfoListDataSource("booleanFormats") });

                // DateTime (Date)
                case "dayFormat":
                    return new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ComboBox")).ComboBox({ listDataSource: createDesignerInfoListDataSource("dayFormats") });
                case "monthFormat":
                    return new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ComboBox")).ComboBox({ listDataSource: createDesignerInfoListDataSource("monthFormats") });
                case "yearFormat":
                    return new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ComboBox")).ComboBox({ listDataSource: createDesignerInfoListDataSource("yearFormats") });

                // DateTime (Time)
                case "amPmFormat":
                    return new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ComboBox")).ComboBox({ listDataSource: createDesignerInfoListDataSource("amPmFormats") });
                case "hourFormat":
                    return new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ComboBox")).ComboBox({ listDataSource: createDesignerInfoListDataSource("hourFormats") });
                case "minuteFormat":
                    return new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ComboBox")).ComboBox({ listDataSource: createDesignerInfoListDataSource("minuteFormats") });
                case "secondFormat":
                    return new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ComboBox")).ComboBox({ listDataSource: createDesignerInfoListDataSource("secondFormats") });
                case "timeFormat":
                    return new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ComboBox")).ComboBox({ listDataSource: createDesignerInfoListDataSource("timeFormats") });

                // Numeric
                case "currencySymbolFormat":
                    return new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ComboBox")).ComboBox({ listDataSource: createDesignerInfoListDataSource("currencySymbolFormats") });
                case "negativeFormat":
                    return new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ComboBox")).ComboBox({ listDataSource: createDesignerInfoListDataSource("negativeFormats") });
            }
        }

        if (classHierarchy.contains("SeriouslyRAD.Framework.Data.Reports.Print.ReportElements.FieldElementSpecial, SeriouslyRAD.Framework.Data.Reports"))
        {
            switch (propertyInfo.name)
            {
                case "color":
                    return new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ColorPicker")).ColorPicker();

                case "type":
                    return new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ComboBox")).ComboBox({ listDataSource: createDesignerInfoListDataSource("fieldElementSpecialTypes") });
            }
        }

        if (classHierarchy.contains("SeriouslyRAD.Framework.Data.Reports.Print.ReportElements.SubreportElement, SeriouslyRAD.Framework.Data.Reports"))
        {
            switch (propertyInfo.name)
            {
                // General
                case "storedProcedureName":
                    return new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ComboBoxApiMethod")).ComboBoxApiMethod({ api: "modules/reports/printreportdesigner", listDataSourceDescriptionProperty: "name", listDataSourceValueProperty: "name", method: "reportstoredprocedures", });
            }
        }

        if (classHierarchy.contains("SeriouslyRAD.Framework.Data.Reports.Print.ReportElements.TextElement, SeriouslyRAD.Framework.Data.Reports"))
        {
            switch (propertyInfo.name)
            {
                // General
                case "color":
                    return new (await import("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ColorPicker")).ColorPicker();
            }
        }
    });

    registerPageManagerConfiguration(configuration, pageManagerConfiguration);
}

function registerDotNetTypes()
{
    DotNetTypeMapper.addTypes(
        [
            //    { typeIdRegex: "SeriouslyRAD.Framework.Data.Reports.Print.ReportElements.TextElement, SeriouslyRAD.Framework.Data.Reports", createFn: async () => (await import("./Server/DotNet/SeriouslyRAD.Framework.Data.Processes/Evaluation/ProcessItemSupportsInterruptOptionEvaluator")).ProcessItemSupportsInterruptOptionEvaluator },
        ]
    );
}

function registerReportElements()
{
    PrintReportDesignerStatic.registerServerTypes(
        [
            { typeIdRegex: "SeriouslyRAD.Framework.Data.Reports.Print.ReportElements.BoxElement, SeriouslyRAD.Framework.Data.Reports", createFn: async () => (await import("./Ui/Controls/PrintReportDesigner/Controls/ReportElements/DrawingElements/BoxElement")).BoxElement as Constructor<ReportElement> },
            { typeIdRegex: "SeriouslyRAD.Framework.Data.Reports.Print.ReportElements.ChartElement, SeriouslyRAD.Framework.Data.Reports", createFn: async () => (await import("./Ui/Controls/PrintReportDesigner/Controls/ReportElements/ChartElement")).ChartElement as Constructor<ReportElement> },
            { typeIdRegex: "SeriouslyRAD.Framework.Data.Reports.Print.ReportElements.FieldElementDatabase, SeriouslyRAD.Framework.Data.Reports", createFn: async () => (await import("./Ui/Controls/PrintReportDesigner/Controls/ReportElements/FieldElements/FieldElementDatabase")).FieldElementDatabase as Constructor<ReportElement> },
            { typeIdRegex: "SeriouslyRAD.Framework.Data.Reports.Print.ReportElements.FieldElementSpecial, SeriouslyRAD.Framework.Data.Reports", createFn: async () => (await import("./Ui/Controls/PrintReportDesigner/Controls/ReportElements/FieldElements/FieldElementSpecial")).FieldElementSpecial as Constructor<ReportElement> },
            { typeIdRegex: "SeriouslyRAD.Framework.Data.Reports.Print.ReportElements.LineElementHorizontal, SeriouslyRAD.Framework.Data.Reports", createFn: async () => (await import("./Ui/Controls/PrintReportDesigner/Controls/ReportElements/DrawingElements/LineElementHorizontal")).LineElementHorizontal as Constructor<ReportElement> },
            { typeIdRegex: "SeriouslyRAD.Framework.Data.Reports.Print.ReportElements.LineElementVertical, SeriouslyRAD.Framework.Data.Reports", createFn: async () => (await import("./Ui/Controls/PrintReportDesigner/Controls/ReportElements/DrawingElements/LineElementVertical")).LineElementVertical as Constructor<ReportElement> },
            { typeIdRegex: "SeriouslyRAD.Framework.Data.Reports.Print.ReportElements.PictureElement, SeriouslyRAD.Framework.Data.Reports", createFn: async () => (await import("./Ui/Controls/PrintReportDesigner/Controls/ReportElements/PictureElement")).PictureElement as Constructor<ReportElement> },
            { typeIdRegex: "SeriouslyRAD.Framework.Data.Reports.Print.ReportElements.SubreportElement, SeriouslyRAD.Framework.Data.Reports", createFn: async () => (await import("./Ui/Controls/PrintReportDesigner/Controls/ReportElements/SubreportElement")).SubreportElement as Constructor<ReportElement> },
            { typeIdRegex: "SeriouslyRAD.Framework.Data.Reports.Print.ReportElements.TextElement, SeriouslyRAD.Framework.Data.Reports", createFn: async () => (await import("./Ui/Controls/PrintReportDesigner/Controls/ReportElements/TextElement")).TextElement as Constructor<ReportElement> },
        ]
    );
}
